/* eslint-disable @typescript-eslint/no-unused-expressions */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC } from "react";
import { LaddaButton as ReactLadda } from "react-ladda-button";
import Dropdown from "react-multilevel-dropdown";
import { IButton } from "../../types/components/button";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuid } from "uuid";

interface IStudyMultiLevelDropdown {
  size: "xl" | "sm";
  items: Array<IButton>;
  show: boolean;
  showSubItem?: boolean;
  data?: unknown;
  position?: Placement;
  showButton?: boolean;
  wrapperClass?: string;
  popoverClassName?: string;
  onClose?: () => void;
  onHandleClick: () => void;
}

export const StudyMultiLevelDropdown: FC<IStudyMultiLevelDropdown> = ({
  items,
  onHandleClick,
  show,
  data,
  showSubItem = false,
  size = "sm",
  position = "left",
  onClose,
  popoverClassName = "",
  wrapperClass = "",
  showButton,
}) => {
  const displaySubmenu = (subMenu: IButton[] = []) => {
    const filterSubitems = subMenu.filter((i) => i.show);

    if (filterSubitems.length === 0) {
      return null;
    }
    return (
      <Dropdown.Submenu
        data-action="action"
        position={
          position as "left" | "right" | "bottom" | "left-top" | "right-top"
        }
        className="submenu-pos-right"
      >
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        {filterSubitems.map((submenuItem) => displayMenuItem(submenuItem))}
      </Dropdown.Submenu>
    );
  };

  const displayMenuItem = (submenuItem: IButton) => {
    if (!submenuItem.show) {
      return null;
    }
    return (
      <Dropdown.Item
        onClick={() => {
          if (submenuItem.disabled) return;
          if (!submenuItem.loading) submenuItem.onHandleClick(data);
          onClose?.();
          setTimeout(() => {
            document.body.click(); // it will make OverlayTrigger close automatically
          }, 0);
        }}
        data-action="action"
        isDisabled={submenuItem.disabled}
        className={`${
          submenuItem.loading || submenuItem.disabled
            ? "cursor-not-allowed"
            : ""
        } submenu multiple-level-item`}
      >
        <div className="d-flex flex-row" data-action="action">
          <ReactLadda
            loading={submenuItem.loading}
            className={`fake-button p-none ${
              !submenuItem.loading ? "bg-transparent" : "bg-loading"
            }`}
            data-style="slide-right"
            disabled={submenuItem.disabled}
          >
            {submenuItem.iconClass ? (
              <i
                data-action="action"
                className={`${submenuItem.iconClass} ${submenuItem.textClass}`}
                style={{ marginRight: "1em" }}
              />
            ) : (
              submenuItem.iconImage
            )}
          </ReactLadda>

          <span data-action="action" className={submenuItem.textClass}>
            {submenuItem.name}
          </span>
        </div>
        {displaySubmenu(submenuItem.subs)}
      </Dropdown.Item>
    );
  };

  const renderDropdown = () => {
    if (!showSubItem) {
      const filterItems = items.filter((i) => i.show);
      return (
        <>
          {filterItems.length > 0 && (
            <div
              className="dropdown position-static show"
              data-action="action"
              key={uuid()}
            >
              <FontAwesomeIcon
                onClick={() => {
                  setTimeout(() => {
                    onHandleClick();
                  }, 50);
                }}
                className="cursor-pointer"
                size={`${showButton ? "xs" : "lg"}`}
                icon="ellipsis-vertical"
              />
              <div
                className={`${
                  show ? "show" : ""
                } dropdown-menu dropdown-menu-right size-${size}`}
                style={{ top: "auto" }}
              >
                <div className="d-flex flex-direction-column ">
                  {filterItems.map((item) => {
                    return (
                      <div
                        data-action="action"
                        className="cursor-pointer dropdown-item"
                        aria-hidden
                        onClick={() => {
                          item.onHandleClick(data);
                          if (onClose) {
                            onClose();
                            setTimeout(() => {
                              document.body.click(); // it will make OverlayTrigger close automatically
                            }, 0);
                          }
                        }}
                      >
                        {item.iconClass ? (
                          <i
                            data-action="action"
                            className={`${item.iconClass} ${item.textClass}`}
                          />
                        ) : (
                          item.iconImage
                        )}

                        <span data-action="action" className={item.textClass}>
                          {item.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
    return (
      <OverlayTrigger
        popperConfig={{ strategy: "fixed" }}
        trigger={["click"]}
        placement={position}
        rootClose
        overlay={
          <Popover
            className={`${popoverClassName} multilevel-popover`}
            id={Date.now().toString()}
          >
            {items.map((dropdownItem) => displayMenuItem(dropdownItem))}
          </Popover>
        }
      >
        <button
          action-name="show-more"
          data-action="action"
          className={`show-more three-nav-button ${wrapperClass} ${
            showButton ? "" : "transparent"
          }`}
          onClick={(e) => {
            // avoid click in row.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FontAwesomeIcon
            className="cursor-pointer p-lr-xxs"
            size="lg"
            icon="ellipsis-vertical"
          />
        </button>
      </OverlayTrigger>
    );
  };

  return <>{renderDropdown()}</>;
};
